import { formatDateString } from '~/functions/parseDateString';
import type { ClubInfo, Tournament, TournamentEntryPayment } from '~/types/types';

/**
 * Shared reading of the `entry` block the public tournaments endpoint attaches to every
 * tournament. The block has three meaningful states:
 *
 *  - accepts_entries=false                 -> tournament is not in the portal's entry
 *                                             form, show no entry UI at all
 *  - accepts_entries=true, is_closed=false -> registration is open, show the CTA
 *  - accepts_entries=true, is_closed=true  -> registration has closed, say so
 *
 * These live here rather than in a page so the WOW Polo homepage cards and the
 * tournaments table cannot drift apart in how they interpret the same payload.
 */

export const TOURNAMENT_ENTRY_PATH = '/tournament-entry';

export type TournamentEntryBadge = {
   label: string;
   tone: 'open' | 'closed';
};

type TournamentEntryFields = Pick<Tournament, 'id' | 'entry'>;

export function isOpenForEntry(tournament: TournamentEntryFields): boolean {
   return Boolean(tournament.entry?.accepts_entries && !tournament.entry?.is_closed);
}

export function isClosedForEntry(tournament: TournamentEntryFields): boolean {
   return Boolean(tournament.entry?.accepts_entries && tournament.entry?.is_closed);
}

export function acceptsEntries(tournament: TournamentEntryFields): boolean {
   return Boolean(tournament.entry?.accepts_entries);
}

export function entryStatusBadge(tournament: TournamentEntryFields): TournamentEntryBadge | null {
   if (!acceptsEntries(tournament)) {
      return null;
   }

   return isClosedForEntry(tournament)
      ? { label: 'CLOSED', tone: 'closed' }
      : { label: 'OPEN', tone: 'open' };
}

/**
 * The API sends registration_closes_at as a date-only string, and resolves it to the end
 * of that day - the advertised date is the last day entries are accepted. The split
 * tolerates a full timestamp, which the field carried previously and can still carry if
 * one is ever written directly.
 */
export function entryCloseDateLabel(tournament: TournamentEntryFields): string | null {
   const iso = tournament.entry?.registration_closes_at;

   if (!iso) {
      return null;
   }

   return formatDateString(iso.split('T')[0]);
}

/**
 * The club as a display string, or null when there is nothing to show.
 *
 * getClubName in tournamentGameHelpers renders a '-' placeholder for a missing club,
 * which suits a table cell but not the entry form, where the club is simply omitted
 * from the row. The club arrives either as an object or, for legacy rows, a bare
 * string - both shapes are handled.
 */
export function clubDisplayName(club: ClubInfo | string | null | undefined): string | null {
   if (!club) {
      return null;
   }

   if (typeof club === 'string') {
      return club.trim() || null;
   }

   return club.name?.trim() || null;
}

/**
 * Entry fee payment.
 *
 * The link is an external page owned by the tournament organiser, so it obeys a different
 * lifecycle from the entry CTA: the CTA hides when registration closes, the payment link
 * does not. A captain who entered before the close date and comes back to pay afterwards
 * has no other route - there is no captain-facing confirmation email carrying the link,
 * so the public tournament list is the only place it exists.
 */

export const DEFAULT_PAYMENT_CTA_LABEL = 'Pay Entry Fee';

type TournamentPaymentFields = Pick<Tournament, 'entry' | 'endDate'>;

/**
 * The payment block, or null when there is nothing to offer.
 *
 * The API validates the URL against an allowlist of Stripe hosts on save, so the host is
 * not re-checked here - a copy of that allowlist would reject a link the moment the API
 * widens it for a merchant custom domain. The https check is kept because it costs
 * nothing and is not coupled to their configuration.
 */
export function validPayment(
   payment: TournamentEntryPayment | null | undefined,
): TournamentEntryPayment | null {
   if (!payment?.url?.startsWith('https://')) {
      return null;
   }

   return payment;
}

export function tournamentPayment(
   tournament: TournamentPaymentFields,
): TournamentEntryPayment | null {
   return validPayment(tournament.entry?.payment);
}

export function paymentCtaLabel(payment: TournamentEntryPayment | null | undefined): string {
   return payment?.label || DEFAULT_PAYMENT_CTA_LABEL;
}

/**
 * Dates arrive date-only (YYYY-MM-DD), which sorts correctly as a string. Comparing the
 * strings rather than parsed Dates keeps the boundary on the viewer's own calendar day:
 * parsing 'YYYY-MM-DD' yields UTC midnight, which would expire a link mid-afternoon for
 * anyone west of UTC.
 */
function toDateKey(date: Date): string {
   const month = String(date.getMonth() + 1).padStart(2, '0');
   const day = String(date.getDate()).padStart(2, '0');

   return `${date.getFullYear()}-${month}-${day}`;
}

function hasFinished(endDate: string | null | undefined, now: Date): boolean {
   if (!endDate) {
      return false;
   }

   return endDate.split('T')[0] < toDateKey(now);
}

/**
 * Payment is offered while the tournament has not finished, whatever the registration
 * state. A tournament with no end date set stays payable: hiding the link is the more
 * expensive mistake, because it is the only route the captain has, and the organiser can
 * always deactivate the link in Stripe.
 */
export function showPaymentLink(
   tournament: TournamentPaymentFields,
   now: Date = new Date(),
): boolean {
   if (!tournamentPayment(tournament)) {
      return false;
   }

   return !hasFinished(tournament.endDate, now);
}

/**
 * The entry form prefills its tournament selection from this query param
 * (see pages/tournament-entry.vue).
 */
export function tournamentEntryRoute(tournament: TournamentEntryFields) {
   return { path: TOURNAMENT_ENTRY_PATH, query: { tournament: tournament.id } };
}
